// =====================================================
// TIME TRACKING / FICHAJE - TYPES
// Conforme a la normativa laboral UE 2019/1152
// =====================================================

// Tipos de eventos de fichaje
export type ClockEventType = 'clock_in' | 'clock_out' | 'break_start' | 'break_end';

// Motivos de pausa según normativa laboral UE
export type BreakReason =
  | 'meal'              // Comida (obligatoria por ley > 6h trabajo)
  | 'rest'              // Descanso (pausas cortas)
  | 'personal'          // Necesidades personales
  | 'medical'           // Médico/Salud
  | 'smoking'           // Fumador (no remunerada)
  | 'breastfeeding'     // Lactancia (remunerada, derecho legal)
  | 'training'          // Formación
  | 'union'             // Actividad sindical
  | 'other';            // Otro motivo

// Registro de fichaje individual
export interface TimeEntry {
  id: string;
  user_id: string;
  date: string; // YYYY-MM-DD
  event_type: ClockEventType;
  event_time: string; // ISO datetime
  break_reason?: BreakReason | null;
  break_type_id?: string | null; // Referencia a break_types existente
  notes?: string | null;
  location?: string | null; // Geolocalización opcional
  device_id?: string | null; // ID del dispositivo (NFC, app, etc.)
  is_manual: boolean; // Si fue entrada manual vs automática
  approved_by?: string | null;
  created_at: string;
  updated_at: string;
  // Expanded relations
  user?: {
    id: string;
    full_name: string;
    employee_id: string | null;
    area: string | null;
  };
  approver?: {
    id: string;
    full_name: string;
  };
}

// Resumen diario de fichaje
export interface DailyTimeSummary {
  id: string;
  user_id: string;
  date: string;
  first_clock_in?: string | null;
  last_clock_out?: string | null;
  total_worked_minutes: number;
  total_break_minutes: number;
  paid_break_minutes: number;
  unpaid_break_minutes: number;
  overtime_minutes: number;
  status: 'pending' | 'complete' | 'incomplete' | 'approved';
  notes?: string | null;
  approved_by?: string | null;
  approved_at?: string | null;
  created_at: string;
  updated_at: string;
  // Expanded
  user?: {
    id: string;
    full_name: string;
    employee_id: string | null;
  };
  entries?: TimeEntry[];
}

// Estado actual del trabajador
export interface WorkerClockStatus {
  user_id: string;
  full_name: string;
  employee_id: string | null;
  area: string | null;
  current_status: 'clocked_out' | 'working' | 'on_break';
  last_event_type: ClockEventType | null;
  last_event_time: string | null;
  today_worked_minutes: number;
  today_break_minutes: number;
  current_break_reason?: BreakReason | null;
  shift_name?: string | null;
  shift_start?: string | null;
  shift_end?: string | null;
}

// Formulario para crear entrada de tiempo
export interface TimeEntryFormData {
  user_id: string;
  event_type: ClockEventType;
  event_time?: string; // Si es manual
  break_reason?: BreakReason | null;
  break_type_id?: string | null;
  notes?: string | null;
  is_manual?: boolean;
}

// Configuración de motivos de pausa
export interface BreakReasonConfig {
  code: BreakReason;
  label: string;
  description: string;
  is_paid: boolean;
  max_duration_minutes?: number;
  requires_approval: boolean;
  icon: string;
  color: string;
}

// Configuración estándar de motivos según normativa UE
export const BREAK_REASONS: BreakReasonConfig[] = [
  {
    code: 'meal',
    label: 'Comida',
    description: 'Pausa para comida (obligatoria > 6h de trabajo)',
    is_paid: false,
    max_duration_minutes: 60,
    requires_approval: false,
    icon: 'Utensils',
    color: '#f59e0b',
  },
  {
    code: 'rest',
    label: 'Descanso',
    description: 'Pausa corta de descanso',
    is_paid: true,
    max_duration_minutes: 15,
    requires_approval: false,
    icon: 'Coffee',
    color: '#8b5cf6',
  },
  {
    code: 'personal',
    label: 'Personal',
    description: 'Necesidades personales',
    is_paid: true,
    max_duration_minutes: 10,
    requires_approval: false,
    icon: 'User',
    color: '#3b82f6',
  },
  {
    code: 'medical',
    label: 'Médico',
    description: 'Consulta médica o malestar',
    is_paid: true,
    max_duration_minutes: undefined,
    requires_approval: true,
    icon: 'Heart',
    color: '#ef4444',
  },
  {
    code: 'smoking',
    label: 'Fumador',
    description: 'Pausa para fumar (no remunerada)',
    is_paid: false,
    max_duration_minutes: 10,
    requires_approval: false,
    icon: 'Wind',
    color: '#6b7280',
  },
  {
    code: 'breastfeeding',
    label: 'Lactancia',
    description: 'Permiso de lactancia (derecho legal remunerado)',
    is_paid: true,
    max_duration_minutes: 60,
    requires_approval: false,
    icon: 'Baby',
    color: '#ec4899',
  },
  {
    code: 'training',
    label: 'Formación',
    description: 'Pausa para formación o capacitación',
    is_paid: true,
    max_duration_minutes: undefined,
    requires_approval: true,
    icon: 'GraduationCap',
    color: '#06b6d4',
  },
  {
    code: 'union',
    label: 'Sindical',
    description: 'Actividad sindical (derecho legal)',
    is_paid: true,
    max_duration_minutes: undefined,
    requires_approval: false,
    icon: 'Users',
    color: '#22c55e',
  },
  {
    code: 'other',
    label: 'Otro',
    description: 'Otro motivo (especificar en notas)',
    is_paid: false,
    max_duration_minutes: undefined,
    requires_approval: true,
    icon: 'MoreHorizontal',
    color: '#9ca3af',
  },
];

// Helper para obtener config de motivo
export function getBreakReasonConfig(code: BreakReason): BreakReasonConfig | undefined {
  return BREAK_REASONS.find((r) => r.code === code);
}

// Helper para formatear minutos a HH:MM
export function formatMinutesToTime(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
}

// Helper para calcular diferencia en minutos
export function calculateMinutesDiff(start: string, end: string): number {
  const startDate = new Date(start);
  const endDate = new Date(end);
  return Math.round((endDate.getTime() - startDate.getTime()) / 60000);
}

// =====================================================
// AUDIT TYPES - RDL 8/2019 Spanish Law Compliance
// =====================================================

// Tipos de acciones de auditoría
export type AuditAction =
  | 'clock_in'
  | 'clock_out'
  | 'break_start'
  | 'break_end'
  | 'manual_entry'
  | 'correction'
  | 'deletion'
  | 'approval'
  | 'signature'
  | 'export'
  | 'view_report';

// Registro de auditoría
export interface AuditRecord {
  id: string;
  event_id: string | null;
  user_id: string;
  action: AuditAction;
  old_data: Record<string, any> | null;
  new_data: Record<string, any> | null;
  reason: string | null;
  ip_address: string | null;
  user_agent: string | null;
  device_info: Record<string, any> | null;
  performed_by: string;
  performed_at: string;
  session_id: string | null;
  // Expanded
  user?: {
    id: string;
    full_name: string;
    employee_id: string | null;
  };
  performer?: {
    id: string;
    full_name: string;
  };
}

// Estado de firma
export type SignatureStatus = 'pending' | 'signed' | 'disputed' | 'approved';

// Método de firma
export type SignatureMethod = 'digital' | 'biometric' | 'pin' | 'drawing';

// Firma de período
export interface TimeSignature {
  id: string;
  user_id: string;
  period_type: 'daily' | 'weekly' | 'monthly';
  period_start: string;
  period_end: string;
  total_hours_worked: number;
  total_hours_break: number;
  total_days_worked: number;
  signature_data: string | null;
  signature_method: SignatureMethod;
  signature_hash: string | null;
  status: SignatureStatus;
  signed_at: string | null;
  employee_notes: string | null;
  ip_address: string | null;
  device_info: Record<string, any> | null;
  created_at: string;
  // Expanded
  user?: {
    id: string;
    full_name: string;
    employee_id: string | null;
  };
}

// Resumen mensual para firma
export interface MonthlyTimeSummary {
  user_id: string;
  month_start: string;
  month_end: string;
  days_worked: number;
  total_hours: number;
  total_break_hours: number;
  earliest_entry: string | null;
  latest_exit: string | null;
  avg_daily_hours: number;
  // Expanded
  user?: {
    id: string;
    full_name: string;
    employee_id: string | null;
  };
  signature?: TimeSignature | null;
}

// Registro de exportación
export interface AuditExport {
  id: string;
  export_type: 'time_entries' | 'daily_summary' | 'monthly_report' | 'full_audit';
  format: 'xlsx' | 'pdf' | 'csv' | 'json';
  user_ids: string[] | null;
  date_from: string;
  date_to: string;
  file_name: string | null;
  file_size_bytes: number | null;
  record_count: number | null;
  purpose: 'internal_audit' | 'labor_inspection' | 'employee_request' | null;
  requested_by: string;
  requested_at: string;
  ip_address: string | null;
  // Expanded
  requester?: {
    id: string;
    full_name: string;
  };
}

// Filtros para auditoría
export interface AuditFilters {
  userId?: string;
  action?: AuditAction;
  dateFrom?: string;
  dateTo?: string;
  performedBy?: string;
}

// Estadísticas de auditoría
export interface AuditStats {
  total_records: number;
  by_action: Record<AuditAction, number>;
  by_user: { user_id: string; full_name: string; count: number }[];
  corrections_count: number;
  signatures_pending: number;
  signatures_completed: number;
  last_export: AuditExport | null;
}
